import { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import SlackChannelPicker, { invalidateSlackChannelsCache } from '../components/SlackChannelPicker';
import { useSearchParams } from 'react-router-dom';

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [slackAuth, setSlackAuth] = useState({ linked: false, workspace_team_name: '', workspace_team_id: '', slack_user_id: '' });
  const [settings, setSettings] = useState({
    slack_bot_token: '',
    slack_channel_id: '',
  });
  const [botTokenSet, setBotTokenSet] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); fetchSlackAuth(); }, []);

  useEffect(() => {
    if (searchParams.get('slack_linked') === '1') {
      toast.success('Slack account linked successfully!');
      searchParams.delete('slack_linked');
      setSearchParams(searchParams, { replace: true });
      fetchSlackAuth();
      checkAuth();
    }
    const err = searchParams.get('slack_error');
    if (err) {
      toast.error(`Slack link failed: ${err.replace(/_/g, ' ')}`);
      searchParams.delete('slack_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, checkAuth]);

  const fetchSlackAuth = async () => {
    try {
      const res = await api.get('/auth/slack/status');
      setSlackAuth(res.data);
    } catch { /* ignore */ }
  };

  const handleLinkSlack = async () => {
    try {
      const res = await api.get('/auth/slack/login?intent=link');
      window.location.href = res.data.authorize_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to start Slack link');
    }
  };

  const handleUnlinkSlack = async () => {
    try {
      await api.post('/auth/slack/unlink');
      toast.success('Slack account unlinked');
      fetchSlackAuth();
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlink');
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setBotTokenSet(!!res.data.slack_bot_token_set);
      setSettings({
        slack_channel_id: res.data.slack_channel_id || '',
        slack_bot_token: '', // never echoed back
      });
    } catch (err) { console.error('Failed to fetch settings:', err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { slack_channel_id: settings.slack_channel_id };
      if (settings.slack_bot_token) payload.slack_bot_token = settings.slack_bot_token;
      await api.put('/settings', payload);
      toast.success('Settings saved');
      // Bot token may have changed — drop cached channel list so pickers refetch.
      invalidateSlackChannelsCache();
      await fetchSettings();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTestSlackBot = async () => {
    try {
      const res = await api.post('/settings/test-slack-bot');
      toast.success(res.data.message || 'Slack Bot test succeeded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Slack Bot test failed'); }
  };

  const isAdmin = user?.role === 'admin';

  const slackAccountCard = (
    <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 mb-4" data-testid="my-slack-account-card">
      <h3 className="text-base font-bold text-[#34495e] mb-1">My Slack Account</h3>
      <p className="text-xs text-[#7F8C8D] mb-4">
        Link your Slack account so task-assignment DMs reach you directly.
      </p>
      {slackAuth.linked ? (
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-[#34495e]">
            <span className="inline-block w-2 h-2 rounded-full bg-[#27AE60] mr-2" />
            Linked to Slack user <code className="text-xs">{slackAuth.slack_user_id}</code>
            {slackAuth.workspace_team_name && <span className="text-[#7F8C8D]"> in <strong>{slackAuth.workspace_team_name}</strong></span>}
          </div>
          <Button variant="outline" onClick={handleUnlinkSlack} className="rounded-sm border-[#e74c3c] text-[#e74c3c] hover:bg-[#e74c3c] hover:text-white" data-testid="unlink-slack-btn">
            Unlink
          </Button>
        </div>
      ) : (
        <div>
          <Button onClick={handleLinkSlack} className="bg-[#4A154B] hover:bg-[#3e1040] text-white rounded-sm" data-testid="link-slack-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.712.133a5.381 5.381 0 00-5.376 5.387 5.381 5.381 0 005.376 5.386h5.376V5.52A5.381 5.381 0 0019.712.133m0 14.365H5.376A5.381 5.381 0 000 19.884a5.381 5.381 0 005.376 5.387h14.336a5.381 5.381 0 005.376-5.387 5.381 5.381 0 00-5.376-5.386" fill="#36C5F0"/>
              <path d="M53.76 19.884a5.381 5.381 0 00-5.376-5.386 5.381 5.381 0 00-5.376 5.386v5.387h5.376a5.381 5.381 0 005.376-5.387m-14.336 0V5.52A5.381 5.381 0 0034.048.133a5.381 5.381 0 00-5.376 5.387v14.364a5.381 5.381 0 005.376 5.387 5.381 5.381 0 005.376-5.387" fill="#2EB67D"/>
              <path d="M34.048 54a5.381 5.381 0 005.376-5.387 5.381 5.381 0 00-5.376-5.386h-5.376v5.386A5.381 5.381 0 0034.048 54m0-14.365h14.336a5.381 5.381 0 005.376-5.386 5.381 5.381 0 00-5.376-5.387H34.048a5.381 5.381 0 00-5.376 5.387 5.381 5.381 0 005.376 5.386" fill="#ECB22E"/>
              <path d="M0 34.249a5.381 5.381 0 005.376 5.386 5.381 5.381 0 005.376-5.386v-5.387H5.376A5.381 5.381 0 000 34.25m14.336 0v14.364A5.381 5.381 0 0019.712 54a5.381 5.381 0 005.376-5.387V34.25a5.381 5.381 0 00-5.376-5.387 5.381 5.381 0 00-5.376 5.387" fill="#E01E5A"/>
            </svg>
            Link my Slack Account
          </Button>
          {isAdmin && !slackAuth.workspace_team_id && (
            <p className="text-[10px] text-[#7F8C8D] mt-2">
              As admin, linking your Slack also sets the workspace all other users must belong to.
            </p>
          )}
        </div>
      )}
    </div>
  );

  if (!isAdmin) {
    return (
      <div data-testid="settings-page">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e] mb-1">Settings</h2>
        <p className="text-sm text-[#7F8C8D] mb-6">Manage your personal integrations</p>
        {slackAccountCard}
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-[#34495e] mb-1">Settings</h2>
      <p className="text-sm text-[#7F8C8D] mb-6">Configure Slack notifications</p>

      {slackAccountCard}

      {/* Slack Bot API (DMs + channel posts) */}
      <div className="bg-white border border-[#E2E8F0] rounded-sm p-6 mb-4">
        <h3 className="text-base font-bold text-[#34495e] mb-1">Slack Bot API (DM + Channel)</h3>
        <p className="text-xs text-[#7F8C8D] mb-4">
          Sends a DM to assignees when a task is created, and a message to the channel when a task is marked Done.
          Requires a Bot User OAuth Token (<code>xoxb-...</code>) with scopes: <code>users:read</code>, <code>users:read.email</code>, <code>chat:write</code>, <code>im:write</code>, <code>channels:read</code>, <code>groups:read</code>.
        </p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">
              Bot User OAuth Token {botTokenSet && <span className="text-[#27AE60] normal-case font-normal ml-2">(configured — leave blank to keep existing)</span>}
            </Label>
            <Input
              type="password"
              value={settings.slack_bot_token}
              onChange={(e) => setSettings(s => ({ ...s, slack_bot_token: e.target.value }))}
              placeholder={botTokenSet ? '••••••••••••••••' : 'xoxb-...'}
              className="mt-1 rounded-sm border-[#E2E8F0]"
              data-testid="slack-bot-token-input"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Channel ID for Completion Messages</Label>
            <div className="mt-1">
              <SlackChannelPicker
                value={settings.slack_channel_id}
                onChange={(val) => setSettings(s => ({ ...s, slack_channel_id: val }))}
                placeholder="Pick a default Slack channel..."
                testId="settings-global-slack-channel"
              />
            </div>
            <p className="text-[10px] text-[#BDC3C7] mt-1">
              Default channel for task-completion posts (tasks can override this individually).
              Invite the bot with <code>/invite @YourBot</code> in the channel.
            </p>
          </div>
          <Button variant="outline" onClick={handleTestSlackBot} className="rounded-sm border-[#34495e] text-[#34495e] hover:bg-[#34495e] hover:text-white" data-testid="test-slack-bot-btn">
            Test Slack Bot
          </Button>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-[#34495e] hover:bg-[#2c3e50] text-white rounded-sm" data-testid="save-settings-btn">
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
}
